/*
 * Cart recommendations
 *
 * Renders Shopify related-product recommendations for the FIRST line item in
 * the cart. The same custom element powers both surfaces (cart drawer and cart
 * page); which sections get re-rendered after an add is taken from whichever
 * Dawn host the element is nested in:
 *
 *   - inside <cart-drawer>  -> CartDrawer.getSectionsToRender()  (cart-drawer, cart-icon-bubble)
 *   - inside <cart-items>   -> CartItems.getSectionsToRender()   (main-cart-items, cart-icon-bubble,
 *                                                                cart-live-region-text, main-cart-footer)
 *
 * The snippet is rendered in both surfaces, so this file can be requested
 * twice on the cart page — the customElements guard keeps the second execution
 * from redefining the element.
 */
if (!customElements.get('cart-recommendations')) {
  class CartRecommendations extends HTMLElement {
    static cache = new Map();

    // Dawn's desktop breakpoint. Keep in sync with the side-panel media query in
    // component-cart-recommendations.css.
    static SIDE_PANEL_MEDIA = '(min-width: 750px)';

    /*
     * Adds in flight, counted on the class rather than the instance.
     *
     * An add tears this surface down and rebuilds it, so the element that starts a
     * request often isn't the one still standing when it finishes. Holding the count
     * statically and re-applying it from connectedCallback means the overlay stays up
     * across those swaps instead of vanishing with the element that raised it.
     */
    static pendingAdds = 0;

    static setBusy(delta) {
      CartRecommendations.pendingAdds = Math.max(0, CartRecommendations.pendingAdds + delta);
      document.querySelectorAll('cart-recommendations').forEach((element) => element.syncBusy?.());
    }

    syncBusy() {
      if (CartRecommendations.pendingAdds > 0) this.setAttribute('aria-busy', 'true');
      else this.removeAttribute('aria-busy');
    }

    constructor() {
      super();

      this.list = this.querySelector('.cart-recommendations__list');
      this.errorTarget = this.querySelector('.cart-recommendations__errors');
      this.sliderButtons = this.querySelector('.cart-recommendations__slider-buttons');
      this.onClick = this.onClick.bind(this);
      this.abortController = null;
    }

    connectedCallback() {
      // Placement first. Relocating re-enters connectedCallback, and the pass that
      // actually moves the element returns here so the re-entrant pass does the setup
      // below exactly once.
      if (this.syncPlacement()) return;

      // An instance built by the re-render an add triggers inherits the overlay.
      this.syncBusy();

      this.sidePanelMedia = window.matchMedia(CartRecommendations.SIDE_PANEL_MEDIA);
      this.onSidePanelChange = () => this.syncPlacement();
      this.sidePanelMedia.addEventListener('change', this.onSidePanelChange);

      this.addEventListener('click', this.onClick);

      // The cart's first line item can change from anywhere (quantity change,
      // remove, an add from another surface), so re-evaluate on every update.
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.onCartUpdate());

      this.productId = this.dataset.productId;

      if (!this.productId) {
        this.hideSelf();
        return;
      }

      this.loadRecommendations(this.productId);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.abortController?.abort();
      if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
      if (this.sidePanelMedia) this.sidePanelMedia.removeEventListener('change', this.onSidePanelChange);
    }

    /*
     * Placement — cart drawer only
     *
     * Above the breakpoint the drawer presentation is a vertical panel beside the
     * drawer, which means living outside `.drawer__inner` (overflow: hidden, and its
     * transform makes it the containing block for fixed descendants too). So the one
     * instance moves between the two hosts instead of being duplicated. All the
     * side-panel styling is scoped to the panel host, so presentation always follows
     * placement — and the cart page instance is never touched, because it has no
     * enclosing <cart-drawer>.
     *
     * Returns true when the element was moved.
     */
    syncPlacement() {
      const drawer = this.closest('cart-drawer');
      if (!drawer) return false;

      const panel = drawer.querySelector('.cart-recommendations-panel');
      const drawerItems = drawer.querySelector('cart-drawer-items');
      if (!panel || !drawerItems) return false;

      const wantsPanel = window.matchMedia(CartRecommendations.SIDE_PANEL_MEDIA).matches;
      const isInPanel = this.parentElement === panel;
      if (wantsPanel === isInPanel) return false;

      if (wantsPanel) {
        const incumbent = panel.firstElementChild;

        // The panel host outlives every Dawn cart re-render, so after one there are two
        // instances: the incumbent still showing real cards, and this fresh copy from
        // Liquid showing placeholders. If the incumbent is already displaying this
        // cart's first item, it is not stale — discarding this copy leaves the panel
        // completely untouched, which is what stops the flicker. Only when the first
        // line item actually changed does the incumbent need replacing, and then the
        // cards are carried over so the swap has no blank frame either.
        if (
          incumbent &&
          incumbent !== this &&
          incumbent.dataset.rendered === 'true' &&
          incumbent.dataset.productId === this.dataset.productId
        ) {
          this.remove();
          return true;
        }

        this.adoptRenderedCards(incumbent);
        panel.replaceChildren(this);
      } else {
        // Moving it out is enough — the panel only ever holds this one element, and
        // `:empty` takes it back out of the layout.
        drawerItems.appendChild(this);
      }

      return true;
    }

    /*
     * Hand the outgoing instance's rendered cards to this one before it is evicted.
     *
     * Only reached when the incumbent really is stale — the cart's first line item
     * changed, so its recommendations are for the wrong product. This instance still
     * shows Liquid's placeholders at that moment and re-renders a beat later (from cache,
     * or after a fetch when the new first item has none cached). Carrying the old cards
     * over means the panel shows the previous products for those frames instead of
     * dropping back to placeholders.
     */
    adoptRenderedCards(previous) {
      if (!this.list || this.dataset.rendered === 'true') return;
      if (!previous || previous === this || previous.dataset?.rendered !== 'true') return;

      const previousList = previous.querySelector('.cart-recommendations__list');
      if (previousList?.children.length) this.list.innerHTML = previousList.innerHTML;
    }

    get limit() {
      const parsed = parseInt(this.dataset.limit, 10);
      if (Number.isNaN(parsed)) return 2;
      return Math.min(Math.max(parsed, 1), 10);
    }

    get intent() {
      return this.dataset.intent || 'related';
    }

    hideSelf() {
      this.hidden = true;

      // The panel host outlives Dawn's re-renders now, so a hidden instance left inside
      // it would hold the panel in the layout as an empty bordered box — most visibly
      // when the cart empties, where Liquid stops rendering the module and nothing
      // arrives to evict this one. Detaching makes the host `:empty` again, which takes
      // it out. Any later cart change re-renders the drawer and brings a fresh instance.
      if (this.parentElement?.classList.contains('cart-recommendations-panel')) this.remove();
    }

    /*
     * Fetching
     */

    recommendationsUrl(productId) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      return `${root}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=${
        this.limit
      }&intent=${encodeURIComponent(this.intent)}`;
    }

    loadRecommendations(productId) {
      const cacheKey = `${productId}:${this.limit}:${this.intent}`;
      const cached = CartRecommendations.cache.get(cacheKey);

      if (cached) {
        this.renderProducts(cached);
        return Promise.resolve();
      }

      this.abortController?.abort();
      this.abortController = new AbortController();

      return fetch(this.recommendationsUrl(productId), { signal: this.abortController.signal })
        .then((response) => response.json())
        .then((data) => {
          const products = Array.isArray(data?.products) ? data.products : [];
          CartRecommendations.cache.set(cacheKey, products);
          if (!this.isConnected) return;
          this.renderProducts(products);
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          console.error(error);
          this.hideSelf();
        });
    }

    onCartUpdate() {
      return fetch(`${routes.cart_url}.js`)
        .then((response) => response.json())
        .then((cart) => {
          if (!this.isConnected) return;

          if (!cart || cart.item_count === 0 || !cart.items?.length) {
            this.hideSelf();
            return;
          }

          const nextProductId = String(cart.items[0].product_id);
          if (nextProductId === String(this.productId) && this.list?.children.length) return;

          this.productId = nextProductId;
          this.dataset.productId = nextProductId;
          return this.loadRecommendations(nextProductId);
        })
        .catch((error) => {
          console.error(error);
        });
    }

    /*
     * Rendering
     */

    renderProducts(products) {
      if (!this.list) return;

      if (!products.length) {
        this.hideSelf();
        return;
      }

      this.list.innerHTML = products.map((product, index) => this.productCardHTML(product, index)).join('');
      this.hidden = false;
      // Marks the track as real cards rather than the Liquid placeholders, so the next
      // instance created by a cart re-render knows it can adopt them.
      this.dataset.rendered = 'true';

      // Slides only exist now, so hand them to Dawn's slider the same way
      // media-gallery.js does after it swaps its own slides out.
      //
      // Not cached in the constructor and typeof-guarded on purpose: custom
      // elements upgrade in tree order, so on the cache-hit path (renderProducts
      // called synchronously from connectedCallback) the nested slider-component
      // is still a plain HTMLElement with no resetPages. Skipping is safe — its
      // own constructor runs next and picks up the slides just injected.
      const slider = this.querySelector('slider-component');
      if (typeof slider?.resetPages === 'function') slider.resetPages();

      // Arrows are pointless when there is a single slide; when they all fit,
      // Dawn's own update() disables both and the CSS dims them.
      if (this.sliderButtons) this.sliderButtons.hidden = products.length < 2;
    }

    productCardHTML(product, index) {
      const variant = (product.variants || []).find((candidate) => candidate.available === true);
      const image = product.featured_image || (product.images || [])[0];
      const url = this.escapeHTML(product.url);
      const price = this.formatMoney(variant ? variant.price : product.price);
      const slideId = this.escapeHTML(`${this.dataset.slidePrefix || 'Slide-CartRecommendations'}-${index + 1}`);

      // Background comes from the card colour scheme the same way card-product.liquid
      // applies it to `.card--card`: `color-{scheme} gradient`.
      const scheme = this.dataset.cardColorScheme;
      const schemeClasses = scheme ? ` color-${this.escapeHTML(scheme)} gradient` : '';

      return `<li id="${slideId}" class="cart-recommendations__item slider__slide${schemeClasses}">
          ${
            image
              ? `<a href="${url}" class="cart-recommendations__media" tabindex="-1" aria-hidden="true">
                  <img src="${this.escapeHTML(this.imageUrl(image, 400))}" alt="" loading="lazy" width="400" height="400">
                </a>`
              : ''
          }
          <div class="cart-recommendations__info">
            <a href="${url}" class="cart-recommendations__title full-unstyled-link">${this.escapeHTML(
              product.title
            )}</a>
            <div class="cart-recommendations__price price">
              <span class="price-item price-item--regular">${price}</span>
            </div>
          </div>
          <button
            type="button"
            class="cart-recommendations__add button button--tertiary"
            data-variant-id="${variant ? variant.id : ''}"
            ${variant ? '' : 'disabled'}
          >
            ${this.escapeHTML(variant ? this.dataset.addLabel : this.dataset.soldOutLabel)}
          </button>
        </li>`;
    }

    imageUrl(source, width) {
      const url = String(source);
      return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
    }

    escapeHTML(value) {
      const entities = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
      return String(value ?? '').replace(/[&<>"']/g, (character) => entities[character]);
    }

    // Recommendation JSON reports prices in cents, matching the rest of the
    // Ajax API. Strings with a decimal point are treated as major units.
    formatMoney(price) {
      const format = this.dataset.moneyFormat || '${{amount}}';
      let cents = price;

      if (typeof cents === 'string') {
        cents = cents.includes('.') ? Math.round(parseFloat(cents) * 100) : parseInt(cents, 10);
      }
      if (typeof cents !== 'number' || Number.isNaN(cents)) return '';

      const withDelimiters = (precision, thousands = ',', decimal = '.') => {
        const parts = (cents / 100).toFixed(precision).split('.');
        const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
        return parts[1] ? whole + decimal + parts[1] : whole;
      };

      const placeholder = /\{\{\s*(\w+)\s*\}\}/;
      const match = format.match(placeholder);

      let value;
      switch (match && match[1]) {
        case 'amount_no_decimals':
          value = withDelimiters(0);
          break;
        case 'amount_with_comma_separator':
          value = withDelimiters(2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = withDelimiters(0, '.');
          break;
        case 'amount_with_apostrophe_separator':
          value = withDelimiters(2, "'");
          break;
        case 'amount_with_space_separator':
          value = withDelimiters(2, ' ', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = withDelimiters(0, ' ');
          break;
        case 'amount_with_period_and_space_separator':
          value = withDelimiters(2, ' ');
          break;
        default:
          value = withDelimiters(2);
      }

      return match ? format.replace(placeholder, value) : value;
    }

    /*
     * Add to cart — reuses Dawn's cart AJAX + Section Rendering API
     */

    onClick(event) {
      const button = event.target.closest('.cart-recommendations__add');
      if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

      event.preventDefault();
      this.addToCart(button);
    }

    // The Dawn element that owns section re-rendering for this surface.
    get surface() {
      const drawer = this.closest('cart-drawer');
      if (drawer) return { host: drawer, type: 'drawer' };

      const cartItems = this.closest('cart-items');
      if (cartItems) return { host: cartItems, type: 'page' };

      return { host: null, type: 'page' };
    }

    addToCart(button) {
      const variantId = button.dataset.variantId;
      if (!variantId) return;

      const { host, type } = this.surface;
      if (!host) return;

      // CartDrawer keys its sections by `id`, CartItems by `section`.
      let sectionsToRender = [];
      try {
        sectionsToRender = host.getSectionsToRender();
      } catch (error) {
        console.error(error);
      }
      const sections = sectionsToRender.map((section) => section.section || section.id);

      this.clearError();
      button.setAttribute('aria-disabled', 'true');
      CartRecommendations.setBusy(1);

      const body = JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections,
        sections_url: window.location.pathname,
      });

      fetch(`${routes.cart_add_url}`, { ...fetchConfig('javascript'), ...{ body } })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.showError(response.description || response.message);
            return;
          }

          if (type === 'drawer') {
            host.setActiveElement(button);
            host.renderContents(response);
          } else {
            this.renderCartPageSections(host, sectionsToRender, response);
          }

          // Returned, not fired and forgotten: Dawn's publish resolves Promise.all over
          // its subscribers, and CartItems.onCartUpdate returns its section refetch. So
          // awaiting this holds the overlay up until the cart has actually finished
          // re-rendering — no guessed timeout.
          return publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-recommendations',
            productVariantId: variantId,
            cartData: response,
          });
        })
        .catch((error) => {
          console.error(error);
          this.showError((window.cartStrings && window.cartStrings.error) || this.dataset.errorLabel);
        })
        .finally(() => {
          // `button` may be detached by now if this surface was re-rendered; harmless.
          button.removeAttribute('aria-disabled');
          CartRecommendations.setBusy(-1);
        });
    }

    renderCartPageSections(cartItems, sectionsToRender, response) {
      if (!response.sections) return;

      sectionsToRender.forEach((section) => {
        const container = document.getElementById(section.id);
        const html = response.sections[section.section];
        if (!container || !html) return;

        const target = container.querySelector(section.selector) || container;
        target.innerHTML = cartItems.getSectionInnerHTML(html, section.selector);
      });
    }

    showError(message) {
      if (this.errorTarget) this.errorTarget.textContent = message || this.dataset.errorLabel || '';
    }

    clearError() {
      if (this.errorTarget) this.errorTarget.textContent = '';
    }
  }

  customElements.define('cart-recommendations', CartRecommendations);
}
